import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
} from '@mui/material';
import { ModelSelector } from './ModelSelector';
import {
  getTextModels,
  getModelCapabilitySummary,
  ModelOption,
} from '../config/modelConfig';

export const REASONING_EFFORTS = [
  { value: 'minimal', label: 'Minimal — cheapest / fastest' },
  { value: 'low', label: 'Low — recommended' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High — slowest / most thorough' },
] as const;

export function isBatchGptModel(model?: string | null): boolean {
  const m = (model || '').toLowerCase();
  return m.startsWith('gpt-') && !m.startsWith('gpt-image');
}

export function isGpt5Model(model?: string | null): boolean {
  const m = (model || '').toLowerCase();
  return m.startsWith('gpt-5') || m.startsWith('gpt-6');
}

export function isBatchGeminiModel(model?: string | null): boolean {
  return (model || '').toLowerCase().startsWith('gemini');
}

export function isUsableBatchModel(model?: string | null): boolean {
  return isBatchGptModel(model) || isBatchGeminiModel(model);
}

/** GPT chat models + Gemini text models. Claude, o-series, image, audio, video are excluded. */
export function getBatchModelOptions(): ModelOption[] {
  return getTextModels()
    .filter((m) => {
      if (m.isAvailable === false) return false;
      return isUsableBatchModel(m.apiName);
    })
    .map((m) => ({
      value: m.apiName,
      label: m.displayName,
      provider: m.provider,
      capabilityLabel: getModelCapabilitySummary(m),
      category: m.category,
      costRating: m.costRating,
      speedRating: m.speedRating,
      intelligenceRating: m.intelligenceRating,
    }));
}

export function parseInputTemplate(raw: unknown): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...(raw as Record<string, any>) };
  return {};
}

export type BatchJobSettingsValue = {
  model: string;
  maxTokens: number;
  reasoningEffort: string;
};

type Props = {
  model: string;
  maxTokens?: number | string | null;
  reasoningEffort?: string | null;
  onChange: (next: BatchJobSettingsValue) => void;
  size?: 'small' | 'medium';
};

const DEFAULT_MAX = 8192;
const MIN_TOKENS = 256;
const MAX_TOKENS = 128000;
const DEFAULT_EFFORT = 'low';
const DEFAULT_MODEL = 'gpt-5.4-mini';

function clampTokens(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_MAX;
  return Math.min(MAX_TOKENS, Math.max(MIN_TOKENS, Math.round(n)));
}

export const BatchJobSettings: React.FC<Props> = ({
  model,
  maxTokens,
  reasoningEffort,
  onChange,
  size = 'small',
}) => {
  const options = getBatchModelOptions();
  const usable = isUsableBatchModel(model);
  const gpt5 = isGpt5Model(model);
  const parsedMax = Number(maxTokens);
  const maxVal = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : DEFAULT_MAX;
  const effort = (reasoningEffort || DEFAULT_EFFORT).toLowerCase();

  const emit = (patch: Partial<BatchJobSettingsValue>) => {
    const nextModel = patch.model ?? (usable ? model : DEFAULT_MODEL);
    const nextGpt5 = isGpt5Model(nextModel);
    onChange({
      model: nextModel,
      maxTokens: patch.maxTokens ?? maxVal,
      reasoningEffort: nextGpt5
        ? (patch.reasoningEffort ?? (gpt5 ? effort : DEFAULT_EFFORT))
        : '',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <Typography variant="overline" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
        Batch job
      </Typography>
      {!usable && !!model && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {model} cannot run as a batch job (Claude and o-series are not supported). Pick a GPT or Gemini text model.
        </Alert>
      )}
      <ModelSelector
        value={usable ? model : DEFAULT_MODEL}
        onChange={(val) => emit({ model: String(val || DEFAULT_MODEL) })}
        options={options}
        size={size}
        label="Batch Model"
        helperText="GPT → OpenAI Batch (~50% off). Gemini → Vertex. Claude is not available."
      />
      <TextField
        size={size}
        type="number"
        label="Max tokens per row"
        value={maxVal}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          emit({ maxTokens: Number.isFinite(n) ? n : DEFAULT_MAX });
        }}
        onBlur={() => {
          const clamped = clampTokens(maxVal);
          if (clamped !== maxVal) emit({ maxTokens: clamped });
        }}
        error={maxVal < MIN_TOKENS || maxVal > MAX_TOKENS}
        inputProps={{ min: MIN_TOKENS, max: MAX_TOKENS, step: 256 }}
        helperText={
          gpt5
            ? `Allowed ${MIN_TOKENS.toLocaleString()}–${MAX_TOKENS.toLocaleString()}. Default ${DEFAULT_MAX.toLocaleString()}. GPT-5 counts hidden reasoning against this cap.`
            : `Allowed ${MIN_TOKENS.toLocaleString()}–${MAX_TOKENS.toLocaleString()}. Default ${DEFAULT_MAX.toLocaleString()}. Output cap for each row.`
        }
      />
      {gpt5 && (
        <FormControl size={size} fullWidth>
          <InputLabel>Reasoning effort</InputLabel>
          <Select
            label="Reasoning effort"
            value={REASONING_EFFORTS.some((o) => o.value === effort) ? effort : DEFAULT_EFFORT}
            onChange={(e) => emit({ reasoningEffort: String(e.target.value) })}
          >
            {REASONING_EFFORTS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mx: 1.75 }}>
            Higher effort is slower and uses more tokens. Low is the default.
          </Typography>
        </FormControl>
      )}
    </Box>
  );
};
